import React, { useState, useEffect, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { X, FileText, Send, Loader, Plus, Trash2, Calculator, Receipt, ShieldCheck, Save } from 'lucide-react';
import { db } from '../../db';
import { syncManager } from '../../services/sync';
import { config } from '../../config';

const InvoiceModal = ({ onClose, transaction }) => {
    const [submitting, setSubmitting] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [originalInvoiceId, setOriginalInvoiceId] = useState('');

    // Live Queries for Reactivity
    const liveUsers = useLiveQuery(() => db.tabData.where('tabName').equals('Users').first());
    const liveProducts = useLiveQuery(() => db.tabData.where('tabName').equals('Master_Products').first());
    const liveBusiness = useLiveQuery(() => db.tabData.where('tabName').equals('Business_Details').first());
    const localUsers = useLiveQuery(() => db.user.toArray());

    const [users, setUsers] = useState([]);
    const [products, setProducts] = useState([]);
    const [businessDetails, setBusinessDetails] = useState({});

    // State to hold derived business details
    const [terms, setTerms] = useState('');
    const [initialTerms, setInitialTerms] = useState('');

    const [billingType, setBillingType] = useState('INVOICE'); // INVOICE or QUOTE
    const [userEmail, setUserEmail] = useState('');
    const [lineItems, setLineItems] = useState([]);
    const [notes, setNotes] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('EFT'); // EFT or CARD
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDate, setDueDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);

    const CURRENCY_SYMBOL = 'R';

    const { subtotal, totalTax, grandTotal } = useMemo(() => {
        const sub = lineItems.reduce((acc, item) => acc + (parseFloat(item.total) || 0), 0);
        const tax = sub * 0.15; // 15% VAT
        return {
            subtotal: sub,
            totalTax: tax,
            grandTotal: sub // Assuming prices are VAT inclusive in the catalog as per UI label
        };
    }, [lineItems]);

    useEffect(() => {
        // Reactive User Mapping
        if (liveUsers?.data && liveUsers.data.length > 0) {
            setUsers(liveUsers.data.filter(u => u.Client_Email).map(u => ({
                Client_Name: u.Client_Name || u.Client_Email,
                Client_Email: u.Client_Email
            })));
        } else if (localUsers && localUsers.length > 0) {
            setUsers(localUsers.map(u => ({ Client_Name: u.email, Client_Email: u.email })));
        }

        // Reactive Product Mapping
        if (liveProducts?.data) {
            setProducts(liveProducts.data.map(p => ({
                id: p.SKU || p.Product_ID,
                name: p.Product_Name,
                price: parseFloat(p.Price_ZAR || p.Price || p.Unit_Price || 0),
                description: p.Description || ''
            })));
        }

        // Reactive Business Details & Terms Mapping
        if (liveBusiness?.data) {
            const bizRows = liveBusiness.data || [];
            const bizData = bizRows.reduce((acc, row) => {
                const key = row.Key || row.Item || row.key;
                const val = row.Value || row.value;
                if (key) {acc[key] = val;}
                return acc;
            }, {});

            setBusinessDetails(bizData);
            // Only set terms if they haven't been edited by the user yet, or if it's the first load
            if (!terms) {
                const fetchedTerms = bizData.Invoice_Terms_Default || bizData.Terms_And_Conditions || bizData.Terms || '';
                setTerms(fetchedTerms);
                setInitialTerms(fetchedTerms);
            }
        }
    }, [liveUsers, liveProducts, liveBusiness, localUsers]);

    // Populate form when editing existing transaction
    useEffect(() => {
        if (transaction) {
            setIsEditMode(true);
            setOriginalInvoiceId(transaction.Invoice_ID || transaction.invoiceId || '');
            
            // Set billing type based on transaction type
            const txType = transaction.type || transaction.Invoice_Type || '';
            if (txType.toLowerCase().includes('quote') || txType === 'Quote') {
                setBillingType('QUOTE');
            } else {
                setBillingType('INVOICE');
            }
            
            // Set client email
            setUserEmail(transaction.Client_Email || transaction.clientEmail || '');
            
            // Set dates
            if (transaction.Invoice_Date || transaction.invoiceDate) {
                const invDate = transaction.Invoice_Date || transaction.invoiceDate;
                setInvoiceDate(typeof invDate === 'string' ? invDate.split('T')[0] : new Date(invDate).toISOString().split('T')[0]);
            }
            if (transaction.Due_Date || transaction.dueDate) {
                const due = transaction.Due_Date || transaction.dueDate;
                setDueDate(typeof due === 'string' ? due.split('T')[0] : new Date(due).toISOString().split('T')[0]);
            }
            
            // Set payment method
            if (transaction.Payment_Method || transaction.paymentMethod) {
                const pm = (transaction.Payment_Method || transaction.paymentMethod).toUpperCase();
                setPaymentMethod(pm.includes('CARD') ? 'CARD' : 'EFT');
            }
            
            // Set notes
            setNotes(transaction.Notes || transaction.notes || '');
            
            // Parse line items from various formats
            let items = [];
            if (transaction.Items && Array.isArray(transaction.Items)) {
                items = transaction.Items;
            } else if (transaction.Line_Items && Array.isArray(transaction.Line_Items)) {
                items = transaction.Line_Items;
            } else if (transaction.lineItems && Array.isArray(transaction.lineItems)) {
                items = transaction.lineItems;
            }
            
            // Transform line items to modal format
            if (items.length > 0) {
                setLineItems(items.map((item, idx) => ({
                    id: item.id || `edit-${idx}`,
                    productId: item.Product_ID || item.productId || item.sku || '',
                    name: item.Product_Name || item.productName || item.name || 'Custom Item',
                    description: item.Description || item.description || '',
                    price: parseFloat(item.Price || item.price || item.Unit_Price || 0),
                    quantity: parseInt(item.Quantity || item.quantity || 1),
                    total: parseFloat(item.Total || item.total || 0)
                })));
            }
        }
    }, [transaction]);

    const bulletizeTerms = () => {
        if (!terms) {return;}
        const lines = terms.split('\n');
        const bulleted = lines.map(line => {
            const trimmed = line.trim();
            if (!trimmed) {return line;}
            if (trimmed.startsWith('•') || trimmed.startsWith('-')) {return trimmed;}
            return `• ${trimmed}`;
        }).join('\n');
        setTerms(bulleted);
    };

    const addProduct = (productId) => {
        const product = products.find(p => p.id === productId);
        if (!product) {return;}

        const newItem = {
            id: Date.now().toString(),
            productId: product.id,
            name: product.name,
            description: product.description,
            price: parseFloat(product.price) || 0,
            quantity: 1,
            total: parseFloat(product.price) || 0
        };

        setLineItems(prev => [...prev, newItem]);
    };

    const updateLineItem = (id, field, value) => {
        setLineItems(prev => prev.map(item => {
            if (item.id === id) {
                const updated = { ...item, [field]: value };
                updated.total = (updated.price || 0) * (updated.quantity || 1);
                return updated;
            }
            return item;
        }));
    };

    const removeLineItem = (id) => {
        setLineItems(prev => prev.filter(item => item.id !== id));
    };

    const handleAction = async (actionType) => {
        if (lineItems.length === 0) {return alert("Please add at least one line item.");}
        if (!userEmail) {return alert("Please select a target client.");}

        setSubmitting(true);
        try {
            let invoiceIDToUse;
            let actionToUse = actionType;

            if (isEditMode && originalInvoiceId) {
                // When editing, use the original invoice ID and 'update' action
                invoiceIDToUse = originalInvoiceId;
                actionToUse = 'update';
            } else {
                // Generate new ID for new invoices/quotes
                const date = new Date();
                const year = date.getFullYear().toString().slice(-2);
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const randomSeq = Math.floor(10000 + Math.random() * 90000);
                const prefix = billingType === 'INVOICE' ? 'INV' : 'QUOTE';
                invoiceIDToUse = `${prefix}-00${year}${month}${randomSeq}`;
            }

            await syncManager.triggerWorkflow('billing/process', {
                billingType,
                userEmail,
                lineItems,
                subtotal,
                totalTax,
                total: grandTotal,
                notes,
                terms,
                invoiceDate,
                dueDate,
                action: actionToUse,
                paymentMethod: paymentMethod,
                salesRepEmail: config.USER_EMAIL,
                InvoiceID: invoiceIDToUse,
                Timestamp: new Date().toISOString(),
                ...(isEditMode && { originalInvoiceId: originalInvoiceId })
            });

            const actionVerb = isEditMode ? 'updated' : (actionType === 'send' ? 'generated and sent' : 'saved successfully');
            alert(`${billingType === 'INVOICE' ? 'Invoice' : 'Quote'} ${actionVerb}!`);

            // Trigger background sync for the T&C update if needed
            if (terms !== initialTerms) {syncManager.syncNow();}

            onClose();
        } catch (error) {
            console.error("Billing submission failed", error);
            alert('Error: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };



    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="bg-slate-50 border-b border-slate-100 p-6 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-2xl ${billingType === 'INVOICE' ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'} shadow-sm`}>
                            {billingType === 'INVOICE' ? <Receipt size={24} /> : <FileText size={24} />}
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">
                                {isEditMode ? 'Edit' : 'Smart'} {billingType === 'INVOICE' ? 'Invoice' : 'Quote'} {isEditMode ? 'Record' : 'Builder'}
                            </h2>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                                {isEditMode ? `Editing: ${originalInvoiceId}` : 'Multi-Product Financial Engine'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                    <div className="space-y-8">
                        {/* Config Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Billing Type</label>
                                <div className="flex p-1 bg-slate-100 rounded-xl">
                                    <button
                                        type="button"
                                        onClick={() => setBillingType('INVOICE')}
                                        className={`flex-1 py-2 px-4 rounded-lg text-xs font-black transition-all ${billingType === 'INVOICE' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500'}`}
                                    >
                                        INVOICE
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setBillingType('QUOTE')}
                                        className={`flex-1 py-2 px-4 rounded-lg text-xs font-black transition-all ${billingType === 'QUOTE' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}
                                    >
                                        QUOTE
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Target Client</label>
                                <select
                                    required
                                    value={userEmail}
                                    onChange={e => setUserEmail(e.target.value)}
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                                >
                                    <option value="">-- Choose User --</option>
                                    {users.map(u => <option key={u.Client_Email} value={u.Client_Email}>{u.Client_Name} ({u.Client_Email})</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Sales Rep Indicator */}
                        <div className="flex items-center justify-between px-2">
                            <span className="text-[10px] font-bold text-slate-400 uppercase">Sales Rep: {config.USER_EMAIL}</span>
                        </div>
                    </div>

                    {/* Payment Method Selector */}
                    <div className="grid grid-cols-1 mb-6">
                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Payment Method</label>
                        <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
                            <button
                                type="button"
                                onClick={() => setPaymentMethod('EFT')}
                                className={`flex-1 py-2 px-4 rounded-lg text-xs font-black transition-all ${paymentMethod === 'EFT' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500'}`}
                            >
                                EFT / CASH
                            </button>
                            <button
                                type="button"
                                onClick={() => setPaymentMethod('CARD')}
                                className={`flex-1 py-2 px-4 rounded-lg text-xs font-black transition-all ${paymentMethod === 'CARD' ? 'bg-white text-rose-500 shadow-sm' : 'text-slate-500'}`}
                            >
                                PAYFAST (CARD)
                            </button>
                        </div>
                    </div>

                    {/* Product Picker */}
                    <div className="bg-slate-50/50 p-6 rounded-3xl border border-dashed border-slate-200">
                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-3 text-center">Catalog Selection</label>
                        <div className="flex gap-2">
                            <select
                                id="product-picker"
                                className="flex-1 p-3 bg-white border border-slate-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                onChange={(e) => {
                                    addProduct(e.target.value);
                                    e.target.value = ''; // Reset
                                }}
                            >
                                <option value="">+ Add item from catalog...</option>
                                {products.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} ({CURRENCY_SYMBOL}{p.price.toFixed(2)})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Line Items Table */}
                    {lineItems.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between px-2">
                                <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                                    <Calculator size={14} className="text-blue-500" />
                                    Line Items
                                </h3>
                                <span className="text-[10px] font-bold text-slate-400 uppercase">{lineItems.length} items</span>
                            </div>

                            <div className="space-y-3">
                                {lineItems.map((item) => (
                                    <div key={item.id} className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-slate-100 group hover:border-blue-100 transition-all shadow-sm">
                                        <div className="flex-1 text-left">
                                            <p className="text-sm font-bold text-slate-800 mb-1">{item.name}</p>
                                            <p className="text-[10px] text-slate-500 line-clamp-2 md:line-clamp-none mb-1">{item.description}</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase">{item.productId}</p>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <div className="w-16">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">Qty</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.quantity}
                                                    onChange={(e) => updateLineItem(item.id, 'quantity', parseInt(e.target.value))}
                                                    className="w-full bg-slate-50 p-2 rounded-lg text-xs font-bold text-slate-700 border-none outline-none focus:ring-2 focus:ring-blue-200 transition-all"
                                                />
                                            </div>
                                            <div className="w-24">
                                                <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">Price ({CURRENCY_SYMBOL})</label>
                                                <input
                                                    type="number"
                                                    value={item.price}
                                                    onChange={(e) => updateLineItem(item.id, 'price', parseFloat(e.target.value))}
                                                    className="w-full bg-slate-50 p-2 rounded-lg text-xs font-bold text-slate-700 border-none outline-none focus:ring-2 focus:ring-blue-200 transition-all"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeLineItem(item.id)}
                                                className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all self-end mb-0.5"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Terms & Internal Notes */}
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                                    {billingType === 'INVOICE' ? 'Invoice Date' : 'Quote Date'}
                                </label>
                                <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all" />
                            </div>
                            <div>
                                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                                    {billingType === 'INVOICE' ? 'Due Date' : 'Valid Until'}
                                </label>
                                <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all" />
                            </div>
                            <div>
                                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Internal Notes</label>
                                <textarea
                                    rows={1}
                                    style={{ height: '46px' }}
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none"
                                    placeholder="Ref Code..."
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    Terms & Conditions
                                    <span className="text-[10px] text-blue-500/50 normal-case font-bold">(Persists in Company Details)</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={bulletizeTerms}
                                    className="text-[10px] bg-blue-50 text-blue-600 px-2 py-1 rounded-md hover:bg-blue-100 transition-colors font-black uppercase"
                                >
                                    Format as Bullets
                                </button>
                            </label>
                            <textarea
                                rows={4}
                                value={terms}
                                onChange={e => setTerms(e.target.value)}
                                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-[11px] leading-relaxed font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                placeholder="Enter standard payment terms, bank details, etc..."
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer / Total */}
            <div className="bg-slate-50 border-t border-slate-100 p-8 shrink-0">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Grand Total ({config.CURRENCY_CODE || 'ZAR'})</p>
                        <h3 className="text-3xl font-black text-slate-900 tracking-tight">{CURRENCY_SYMBOL} {grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</h3>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1 flex items-center justify-end gap-2">
                            <ShieldCheck size={12} /> Secure Generation
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase">VAT Included (15%)</p>
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        onClick={() => handleAction('save')}
                        disabled={submitting || lineItems.length === 0}
                        className={`flex-1 py-4 px-6 rounded-2xl bg-white border-2 border-slate-200 text-slate-700 font-black uppercase tracking-widest text-xs flex items-center justify-center gap-3 transition-all hover:bg-slate-50 active:scale-95 disabled:opacity-50`}
                    >
                        {submitting ? <Loader className="animate-spin" size={18} /> : (
                            <>
                                <Save size={18} />
                                Save Draft
                            </>
                        )}
                    </button>

                    <button
                        onClick={() => handleAction('send')}
                        disabled={submitting || lineItems.length === 0}
                        className={`flex-[2] py-4 px-6 rounded-2xl text-white font-black uppercase tracking-[0.2em] text-xs flex items-center justify-center gap-3 transition-all shadow-xl active:scale-95 ${billingType === 'INVOICE'
                            ? 'bg-slate-900 shadow-slate-200 hover:bg-black'
                            : 'bg-blue-600 shadow-blue-200 hover:bg-blue-700'
                            } disabled:opacity-50 disabled:scale-100 disabled:shadow-none`}
                    >
                        {submitting ? (
                            <Loader className="animate-spin" size={18} />
                        ) : (
                            <>
                                <Send size={18} />
                                Finalize & Send
                            </>
                        )}
                    </button>
                </div>

                <p className="text-center text-[9px] text-slate-300 font-bold uppercase tracking-widest mt-6">
                    BizBuilder Financial Orchestration Engine • v20.x
                </p>
            </div>
        </div>

    );
};

export default InvoiceModal;
